// Libs
import classNames from 'classnames/bind';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid2, Stack } from '@mui/material';
import { useCallback, useEffect } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
// Components, Layouts, Pages
import { BaseDatePicker, BaseInput, BaseSelect } from '~/components/common';
// Others
import { IOrderDetail } from '~/utils/interface/order';
import { customToast, formatCurrencyUSD } from '~/utils/helper/common';
import { defaultValues, schema } from './helper';
import { IOpaqueDataPayment, IPaymentQuote, IQuoteDetail } from '~/utils/interface/quotes';
import { IBaseOption, IResultCallApiFn } from '~/utils/interface/common';
import { EMPTY_STRING, optionPaymentMethod } from '~/utils/constants/common';
import { DepositMadeEnum, PaymentAmountEnum, PaymentMethodEnum, ToastTypeEnum } from '~/utils/enum/common';
import { paymentApiLoginId, paymentClientKey, paymentSrc } from '~/utils/constants/env';

// Styles, images, icons
import styles from './FormPaymentQuote.module.scss';

type Props = {
  quoteDetail: IQuoteDetail | IOrderDetail;
  onSubmit: (body: IPaymentQuote) => void;
  onClose: () => void;
};

const cx = classNames.bind(styles);

const FormPaymentQuote = (props: Props) => {
  //#region Destructuring Props
  const { onSubmit, onClose, quoteDetail } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/quotes', 'common/base']);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    trigger,
    formState: { errors },
  } = useForm<IPaymentQuote>({
    resolver: yupResolver(schema(t)),
    defaultValues: defaultValues,
  });

  const { amount, amountType, paymentMethod } = watch();
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    const script = document.createElement('script');
    script.src = `${paymentSrc}`;
    script.async = true;
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);
  //#endregion Implement Hook

  //#region Handle Function
  const handlePaymentCard = (data: IPaymentQuote): Promise<IOpaqueDataPayment | null> => {
    const { cardNumber, cvc, cardExp } = data;
    const [month, year] = cardExp?.split('/') || [];

    // @ts-ignore
    if (!window.Accept) {
      console.error('Accept.js not loaded yet');
      return Promise.resolve(null);
    }

    return new Promise((resolve) => {
      const responseHandler = (response: any) => {
        if (response.messages.resultCode === 'Error') {
          response.messages.message.forEach((m: any) => {
            console.error(`${m.code}: ${m.text}`);
            customToast(ToastTypeEnum.ERROR, `${m.text}`);
          });
          resolve(null);
          return;
        }

        const opaqueData = response.opaqueData;

        resolve({
          dataDescriptor: opaqueData.dataDescriptor,
          dataValue: opaqueData.dataValue,
        });
      };

      // @ts-ignore
      Accept.dispatchData(
        {
          cardData: {
            cardNumber: cardNumber?.replace(/\s+/g, ''),
            month,
            year,
            cardCode: cvc,
          },
          authData: {
            clientKey: paymentClientKey,
            apiLoginID: paymentApiLoginId,
          },
        },
        responseHandler
      );
    });
  };

  const fetchOptionsPaymentMethod = async (): Promise<IResultCallApiFn<IBaseOption>> => ({
    optionsResp: optionPaymentMethod,
    hasMoreResp: false,
  });

  const fetchOptionsPaymentAmount = useCallback(async (): Promise<IResultCallApiFn<IBaseOption>> => {
    const options = handleConvertPaymentAmount();

    const result: IResultCallApiFn<IBaseOption> = {
      optionsResp: options,
      hasMoreResp: false,
    };

    return result;
  }, [quoteDetail]);

  const handleConvertPaymentAmount = () => {
    const options: IBaseOption[] = [
      {
        label: `${formatCurrencyUSD({ value: quoteDetail?.amountDue, showDecimal: true })} Order Balance`,
        value: PaymentAmountEnum.ORDER_BALANCE,
      },
      ...(Number(quoteDetail?.depositMadeAmount) > 0
        ? [
            {
              label: `${formatCurrencyUSD({ value: quoteDetail?.depositMadeAmount, showDecimal: true })} Deposit (${
                quoteDetail?.depositMade === DepositMadeEnum.RENTAL_ORDER_DEPOSIT ? 30 : 50
              }%)`,
              value: PaymentAmountEnum.DEPOSIT,
            },
          ]
        : []),
      {
        label: 'Other Amount',
        value: PaymentAmountEnum.OTHER_AMOUNT,
      },
    ];

    return options;
  };

  const handleSubmitForm = async (data: IPaymentQuote) => {
    const { cardExp, cardNumber, cvc, ...newData } = data;

    if (newData?.paymentMethod === PaymentMethodEnum.CARD) {
      const opaqueData = await handlePaymentCard(data);

      if (!opaqueData) return;

      newData.dataDescriptor = opaqueData?.dataDescriptor;
      newData.dataValue = opaqueData?.dataValue;
    }

    onSubmit(newData);
  };
  //#endregion Handle Function

  return (
    <Dialog id='formPaymentQuoteComponent' open={true} onClose={onClose} fullWidth maxWidth={'sm'}>
      <Box component={'form'} onSubmit={handleSubmit(handleSubmitForm)}>
        <DialogTitle>{t('payment_quote_title')}</DialogTitle>

        <DialogContent dividers={true}>
          <Stack direction={'column'} spacing={4}>
            <Controller
              name={'paymentMethod'}
              control={control}
              render={({ field: { onChange, value } }) => (
                <BaseSelect
                  required
                  gap={8}
                  styleType='contained'
                  value={value}
                  onChange={(opt) => onChange(opt?.value)}
                  label={t('payment_quote_title_payment_method')}
                  callApiFn={fetchOptionsPaymentMethod}
                  errorMessage={errors.paymentMethod?.message}
                />
              )}
            />

            <Controller
              name={'amountType'}
              control={control}
              render={({ field: { onChange, value } }) => (
                <BaseSelect
                  required
                  gap={8}
                  styleType='contained'
                  value={value}
                  onChange={(opt) => {
                    onChange(opt?.value);
                    opt?.value === PaymentAmountEnum.ORDER_BALANCE && setValue('amount', quoteDetail?.amountDue);
                    opt?.value === PaymentAmountEnum.DEPOSIT && setValue('amount', quoteDetail?.depositMadeAmount);
                    opt?.value === PaymentAmountEnum.OTHER_AMOUNT && setValue('amount', undefined);
                  }}
                  label={t('payment_quote_title_payment_amount')}
                  callApiFn={fetchOptionsPaymentAmount}
                  errorMessage={errors.amountType?.message}
                />
              )}
            />

            {amountType === PaymentAmountEnum.OTHER_AMOUNT && (
              <Controller
                control={control}
                name='amount'
                render={({ field: { onChange, value } }) => (
                  <BaseInput
                    label={t('payment_quote_title_amount')}
                    value={value || EMPTY_STRING}
                    onChange={onChange}
                    required
                    type='number'
                    min={0}
                    errorMessage={errors.amount?.message}
                  />
                )}
              />
            )}

            {paymentMethod === 'Card' && (
              <>
                <Controller
                  control={control}
                  name='cardNumber'
                  render={({ field: { onChange, value } }) => (
                    <BaseInput
                      label={t('payment_quote_title_card_number')}
                      placeholder={t('payment_quote_placeholder_card_number')}
                      value={value || EMPTY_STRING}
                      onChange={(e) => {
                        let input = e.target.value.replace(/\D/g, '');

                        if (input.length > 16) input = input.slice(0, 16);

                        const formatted = input.replace(/(.{4})/g, '$1 ').trim();

                        onChange(formatted);
                      }}
                      errorMessage={errors.cardNumber?.message}
                    />
                  )}
                />

                <Grid2 container spacing={4}>
                  <Grid2 size={8}>
                    <Controller
                      control={control}
                      name='cardExp'
                      render={({ field: { onChange, value } }) => (
                        <BaseInput
                          label={t('payment_quote_title_expiry_date')}
                          placeholder={t('payment_quote_placeholder_expiry_date')}
                          value={value || EMPTY_STRING}
                          onChange={(e) => {
                            let input = e.target.value.replace(/\D/g, '');
                            if (input.length > 4) input = input.slice(0, 4);

                            if (input.length > 2) {
                              input = input.slice(0, 2) + '/' + input.slice(2);
                            }

                            onChange(input);
                          }}
                          errorMessage={errors.cardExp?.message}
                        />
                      )}
                    />
                  </Grid2>

                  <Grid2 size={4}>
                    <Controller
                      control={control}
                      name='cvc'
                      render={({ field: { onChange, value } }) => (
                        <BaseInput
                          label={t('payment_quote_title_cvc')}
                          placeholder={t('payment_quote_placeholder_cvc')}
                          value={value || EMPTY_STRING}
                          onChange={(e) => {
                            let input = e.target.value.replace(/\D/g, '');
                            if (input.length > 3) input = input.slice(0, 3);
                            onChange(input);
                          }}
                          errorMessage={errors.cvc?.message}
                        />
                      )}
                    />
                  </Grid2>
                </Grid2>
              </>
            )}

            <Controller
              control={control}
              name='paymentDate'
              render={({ field: { onChange, value } }) => (
                <BaseDatePicker
                  label={t('payment_quote_title_payment_date')}
                  value={value || EMPTY_STRING}
                  onChange={onChange}
                  errorMessage={errors.paymentDate?.message}
                />
              )}
            />

            <Controller
              control={control}
              name={'note'}
              render={({ field: { value, onChange } }) => (
                <BaseInput
                  styleTypeLabel='input'
                  multiline
                  height={'auto'}
                  rows={4}
                  label={t('payment_quote_title_payment_notes')}
                  value={value || EMPTY_STRING}
                  onChange={onChange}
                />
              )}
            />
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button className={cx('buttonCancel')} variant='outlined' onClick={onClose}>
            {t('common_action_cancel', { ns: 'common/base' })}
          </Button>

          <Button variant='contained' className={cx('buttonAdd')} type='submit'>
            {t('common_action_save', { ns: 'common/base' })}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default FormPaymentQuote;
